"use client";

import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function CardCarousel({
    items,
    itemClassName,
    gapClassName = "gap-5 sm:gap-6",
    ariaLabel = "items",
}: {
    /** Each entry is one slide's content; the carousel wraps it with width + snap. */
    items: ReactNode[];
    /** Tailwind width classes controlling how many cards show per breakpoint. */
    itemClassName: string;
    gapClassName?: string;
    ariaLabel?: string;
}) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const update = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanPrev(scrollLeft > 4);
        setCanNext(scrollLeft < scrollWidth - clientWidth - 4);

        const first = el.firstElementChild as HTMLElement | null;
        if (!first) return;
        const base = first.offsetLeft;
        let idx = 0;
        let min = Infinity;
        for (let i = 0; i < el.children.length; i++) {
            const child = el.children[i] as HTMLElement;
            const dist = Math.abs(child.offsetLeft - base - scrollLeft);
            if (dist < min) {
                min = dist;
                idx = i;
            }
        }
        setActiveIndex(idx);
    }, []);

    // Olcum (offsetLeft / scrollWidth) bir sonraki kareye ertelenir ve kare
    // basina bire indirilir: ilk render'da ve her scroll olayinda senkron
    // olcum "zorunlu yeniden duzenleme" (forced reflow) yaratiyordu.
    const frameRef = useRef(0);
    const scheduleUpdate = useCallback(() => {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(update);
    }, [update]);

    useEffect(() => {
        scheduleUpdate();
        const el = trackRef.current;
        if (!el) return;
        el.addEventListener("scroll", scheduleUpdate, { passive: true });
        window.addEventListener("resize", scheduleUpdate);
        return () => {
            cancelAnimationFrame(frameRef.current);
            el.removeEventListener("scroll", scheduleUpdate);
            window.removeEventListener("resize", scheduleUpdate);
        };
    }, [scheduleUpdate]);

    const scrollToIndex = useCallback((i: number) => {
        const el = trackRef.current;
        if (!el) return;
        const first = el.firstElementChild as HTMLElement | null;
        const child = el.children[i] as HTMLElement | undefined;
        if (!first || !child) return;
        el.scrollTo({ left: child.offsetLeft - first.offsetLeft, behavior: "smooth" });
    }, []);

    const step = useCallback(
        (dir: 1 | -1) => scrollToIndex(Math.min(Math.max(activeIndex + dir, 0), items.length - 1)),
        [activeIndex, scrollToIndex, items.length]
    );

    return (
        <>
            <div
                ref={trackRef}
                className={`-mx-4 flex snap-x snap-mandatory ${gapClassName} overflow-x-auto px-4 pb-2 touch-pan-x overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
            >
                {items.map((node, i) => (
                    <div key={i} className={`shrink-0 snap-start ${itemClassName}`}>
                        {node}
                    </div>
                ))}
            </div>

            {/* Controls: prev · dots · next */}
            <div className="mt-8 flex items-center justify-center gap-4 sm:gap-5">
                <button
                    type="button"
                    onClick={() => step(-1)}
                    disabled={!canPrev}
                    aria-label={`Previous ${ariaLabel}`}
                    className="w-11 h-11 flex items-center justify-center bg-white border border-gray-200 text-[#0f4c81] rounded-full hover:bg-[#0f4c81] hover:text-white hover:border-[#0f4c81] transition-all shadow-sm disabled:opacity-40 disabled:pointer-events-none"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2">
                    {items.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => scrollToIndex(i)}
                            aria-label={`Go to ${ariaLabel} ${i + 1}`}
                            aria-current={i === activeIndex}
                            // Gorunen nokta 8px kalir; buton 24px'lik erisilebilir
                            // dokunma hedefi olur (WCAG 2.5.8). Nokta ic span'de.
                            className="group flex h-6 min-w-6 items-center justify-center rounded-full"
                        >
                            <span
                                className={`block h-2 rounded-full transition-all duration-300 ${
                                    i === activeIndex
                                        ? "w-6 bg-[#00AEEF]"
                                        : "w-2 bg-[#0f4c81]/25 group-hover:bg-[#0f4c81]/45"
                                }`}
                            />
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={() => step(1)}
                    disabled={!canNext}
                    aria-label={`Next ${ariaLabel}`}
                    className="w-11 h-11 flex items-center justify-center bg-[#00AEEF] text-white rounded-full hover:bg-[#009bd5] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-40 disabled:pointer-events-none"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
        </>
    );
}
